import { BusinessMinimalOutSchema } from "@/api/client";
import { handleError } from "@/api/handleError";
import { getBusiness } from "@/api/requests/business";
import { getTeamMemberV2 } from "@/api/requests/teamMembers";
import { getUserBusiness } from "@/api/requests/users";
import { BusinessSubscription, TeamMemberDetails } from "@/helpers";
import {
  businessIndustries,
  businessRoles,
  PINIA_PERSIST_OPTIONS,
} from "@/helpers/constants";
import {
  FullBusinessProfile,
  Profile,
  Profiles,
} from "@/helpers/types/business";
import router from "@/router";
import { notify } from "@kyvg/vue3-notification";
import { defineStore } from "pinia";
import { useNumberStore } from "./numbers.store";
import { useUsersStore } from "./users.store";

export interface FullProfile extends TeamMemberDetails {
  business?: FullBusinessProfile | null;
}

export interface BusinessList {
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  business: {
    balance: number | string;
    name: string;
    business_timezone: string;
    id: number;
    verified?: true;
    has_provisioned_first_number: boolean;
  };
  user: number;
  business_name: string;
  date_created: string;
  has_submitted_weekly_survey?: boolean;
}
interface BusinessState {
  businesses?: Profiles | null;
  activeBusinessMinimal?: BusinessMinimalOutSchema | null;
  activeBusiness?: FullBusinessProfile | null;
  roles: string[];
  industryList: string[];
  activeProfileForm: string;
  activeTasks: Set<string>;
  subscriptionDetails?: BusinessSubscription | null;
  activeUserProfile?: TeamMemberDetails | null;
  currentBalance: number;
}
export const useBusinessStore = defineStore("business", {
  state: (): BusinessState => ({
    businesses: null, // list all business under this agent
    activeBusiness: null,
    roles: businessRoles,
    industryList: businessIndustries,
    activeProfileForm: "account",
    activeTasks: new Set(),
    subscriptionDetails: null,
    activeUserProfile: null,
    currentBalance: 0,
    activeBusinessMinimal: null,
  }),
  getters: {
    currentBusinessId(): number | null | undefined {
      // current business id.
      return this.activeBusinessMinimal?.id;
    },
    activeProfile(): FullProfile | undefined {
      // return current business after update. filter the businesslist based on the id of active business since the businesslist is more reactive. This fixes the issue of profile not updateing immediately
      if (!this.activeUserProfile?.profile || !this.activeBusiness) {
        return undefined;
      }

      const fullProfile: FullProfile = {
        ...(this.activeUserProfile || {}),
        business: this.activeBusiness,
      };

      return fullProfile;
    },
    currentUserId(): number | undefined | null {
      const userStore = useUsersStore();

      return userStore.currentUserBusinessLevel?.id;
    },
    getInactiveBusinesses: (state) =>
      state.businesses?.filter(
        (business) => business?.id !== state.activeBusinessMinimal?.id
      ),
    listOfReceivers: () => {
      return Array.from({ length: 10 }, (_, k) => k + 1);
    },
    goGlobalFirstSignIn(): boolean | undefined {
      return (
        this.activeProfile?.business?.is_go_global &&
        !this.activeProfile?.business?.has_filled_engagement_form
      );
    },
  },
  actions: {
    async getBusiness() {
      // retrieve the list of businesses
      const userStore = useUsersStore();
      try {
        const response = await getUserBusiness({ pageSize: 10 });

        const businessData = response?.data?.data;

        this.businesses = businessData;

        userStore.profiles = businessData;

        if (businessData?.length && !this.activeBusinessMinimal) {
          // sets the first element in the array to the active number if no active number exists
          this.activeBusinessMinimal = this.businesses[0];
        }
        await this.fetchExtraBusinessDetails();
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "Error fetching business list",
          silent: true,
        });
      }
    },
    async setActiveBusiness(business: Profile, refresh: boolean = true) {
      const numberStore = useNumberStore();
      const userStore = useUsersStore();
      try {
        numberStore.activeNumber = null;
        numberStore.activeBusinessNumber = null;
        numberStore.activeBusinessNumberForReports = null;

        const userId = userStore.currentUserBusinessLevel?.id;

        if (!userId) {
          throw new Error("User ID not found");
        }

        this.activeBusinessMinimal = business;

        await Promise.all([
          this.fetchActiveUserProfile(business.id, userId),
          this.fetchExtraBusinessDetails(),
          numberStore.getUserPhonesFromBusiness(),
        ]);

        userStore.user_role = this.activeUserProfile?.profile?.role || "";

        notify({
          type: "success",
          duration: 1500,
          title: `Business Transition Successful`,
          text: `Your primary business has successfully transitioned to ${business?.name}.`,
        });

        if (refresh) {
          if (window.location.href.includes("add-new-call-flow")) {
            await router.push("/managers/incoming-flow");

            setTimeout(() => {
              router.go(0);
            }, 100);
          } else {
            router.go(0);
          }
        }
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "Error switching business profile",
        });
      }
    },
    async fetchActiveUserProfile(
      businessId?: number | null,
      userId?: number | null
    ) {
      const userStore = useUsersStore();
      const businessIdToUse = businessId || this.currentBusinessId;
      const userIdToUse = userId || this.currentUserId;

      if (!businessIdToUse) {
        throw new Error("Business ID not found");
      }

      if (!userIdToUse) {
        throw new Error("User ID not found");
      }

      try {
        const response = await getTeamMemberV2({
          businessId: businessIdToUse,
          userId: userIdToUse,
        });

        this.activeUserProfile = {
          ...this.activeUserProfile,
          ...response.data,
        };

        // update current user business level with user bio-data
        const { first_name, last_name, email, mobile } =
          this.activeUserProfile.profile;

        userStore.setCurrentUser({
          ...userStore.currentUserBusinessLevel,
          first_name: first_name || "",
          last_name: last_name || "",
          mobile: mobile || "",
          email: email || "",
        });

        userStore.permissions = response.data.permission;
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "Error fetching active user profile",
        });

        throw error;
      }
    },
    async fetchExtraBusinessDetails() {
      try {
        const userStore = useUsersStore();

        const businessId = this.currentBusinessId;

        if (!businessId) {
          throw new Error("Business ID not found");
        }

        const response = await getBusiness({ businessId });

        this.activeBusiness = response.data as FullBusinessProfile;

        // on refresh fetch the user profile
        await this.fetchActiveUserProfile(
          this.activeBusinessMinimal?.id,
          userStore.currentUserBusinessLevel?.id
        );
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "Error fetching business profile",
          silent: true,
        });
      }
    },
    async refreshActiveBusiness() {
      // when a new business is bought
      const userStore = useUsersStore();

      if (userStore.userProfile) {
        const business = userStore?.userProfile[0];

        await this.setActiveBusiness(business, false);
      }
    },
    // function to update object with ongoing user action
    startTask(taskName: string) {
      this.activeTasks.add(taskName);
    },
    // Function to remove ongoing user action from board
    finishTask(taskName: string) {
      this.activeTasks.delete(taskName);
    },
  },
  persist: PINIA_PERSIST_OPTIONS,
});
